import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useDocument } from 'react-firebase-hooks/firestore';
import { useUserWithRole } from 'hooks/custom-auth';
import { Button, Avatar, Divider, Typography, Tag } from 'antd';
import { PhoneOutlined } from '@ant-design/icons';
import moment from 'moment';

import Client from 'containers/app/client';
import { LayoutPage } from 'components/layouts';
import LoadingFullPage from 'components/loading-full-page';

import BookingUtils from 'utils/bookings';
import BookingRequestActions from './booking-request-actions';
import BookingRequestRate from './booking-request-rate';

import './style.css';

const { getColorByStatus } = BookingUtils;

const getEmployer = (doc) => {
  if (!doc) return null;
  if (!doc.data()) return null;
  return doc.data().userId;
};

const getWorker = (doc) => {
  if (!doc) return null;
  if (!doc.data()) return null;
  return doc.data().worker;
};

const BookingDetail = () => {
  const { id } = useParams();

  const [user, loadingUser, errorUser] = useUserWithRole(Client.auth);
  const ref = Client.db.doc(`bookings/${id}`);
  const [doc, loading, error] = useDocument(ref);
  const employerId = getEmployer(doc);
  const employerRef = employerId ? Client.dbDoc(`users/${employerId}`) : null;
  const [docEmployer, loadingEmployer, errorEmployer] = useDocument(
    employerRef
  );
  const workerId = getWorker(doc);
  const workerRef = workerId ? Client.dbDoc(`users/${workerId}`) : null;
  const [docWorker, loadingWorker, errorWorker] = useDocument(workerRef);

  if (loading || loadingUser || loadingEmployer || loadingWorker)
    return <LoadingFullPage />;
  if (error) throw error;
  if (errorUser) throw errorUser;
  if (errorEmployer) throw errorEmployer;
  if (errorWorker) throw errorWorker;

  if (!docEmployer || !docWorker) return <LoadingFullPage />;

  const { role } = user.claims;
  const request = doc.data();
  const employer = docEmployer.data();
  const worker = docWorker.data();

  const { status = 'pending', dateStart, dateEnd, notes, rate } = request;
  const expired = moment(dateStart).isBefore(moment());
  const fStatus = expired ? 'expired' : status;

  return (
    <LayoutPage className="booking-detail-page">
      <LayoutPage.Container className="wrapper">
        <header className="booking-detail-header">
          <div className="booking-users-wrapper">
            
            {(role === 'isWorker' && (
              <div className="booking-user-wrapper -employer">
                <h3>Employer</h3>
                <Link to={`/employer-detail/${employerId}`}>
                  <Avatar src={employer.info.photoURL} size={150} />
                </Link>
                <h2>
                  {employer.info.firstName} {employer.info.lastName}
                </h2>
                <Button
                  href={`tel:${employer.info.mobilePhone}`}
                  icon={<PhoneOutlined />}
                >
                  {employer.info.mobilePhone}
                </Button>
              </div>
            )) || (
              <div className="booking-user-wrapper -worker">
                <h3>Worker</h3>
                <Link to={`/worker-detail/${workerId}`}>
                  <Avatar src={worker.info.photoURL} size={150} />
                </Link>
                <h2>
                  {worker.info.firstName} {worker.info.lastName}
                </h2>
                <Button
                  href={`tel:${worker.info.mobilePhone}`}
                  icon={<PhoneOutlined />}
                >
                  {worker.info.mobilePhone}
                </Button>
              </div>
            )}

          </div>
        </header>

        <Divider />

        <header className="booking-detail-header">
          <h1>
            Shift <Tag color={getColorByStatus(fStatus)}>{fStatus}</Tag>
          </h1>
        </header>

        <section className="booking-detail-section -body">
          <Typography>
            <p>
              Booking request for{' '}
              <strong>{moment(dateStart).format('dddd DD of MMMM')}</strong> at{' '}
              <strong>{moment(dateStart).format('HH:mm')}</strong> until the{' '}
              <strong>{moment(dateEnd).format('dddd DD of MMMM')}</strong> at{' '}
              <strong>{moment(dateEnd).format('HH:mm')}</strong>
            </p>
            <p>
              With a proposed hour Rate of <strong>£{rate}</strong>
            </p>
          </Typography>

          {notes ? (
            <Typography>
              <i>"{notes}"</i>
            </Typography>
          ) : null}
        </section>

        <BookingRequestActions id={id} request={request} status={fStatus} />
        <BookingRequestRate id={id} request={request} status={fStatus} />
      </LayoutPage.Container>
    </LayoutPage>
  );
};

export default BookingDetail;
